import { useQuery, useMutation, useQueryClient, type UseMutationOptions } from '@tanstack/react-query';
import { serviceApiV3 } from '@/api/client';
import { mediaBase, type FileTransferConfig } from './configs';
import type { OutputStorageConfig, OutputStorageType, CreateOutputStoragePayload } from './shared-storage-types';

// ============================================
// Query keys
// ============================================

export const sharedStorageKeys = {
  config: (prefix: string, serviceId: string) =>
    [prefix, 'output_config', { serviceId }] as const,
};

// ============================================
// Queries
// ============================================

/** Fetch all output storage configurations (s3/ftp/sftp/http/webdav). */
export function useOutputStorageConfig(
  serviceId: string,
  config: FileTransferConfig,
) {
  return useQuery<OutputStorageConfig>({
    queryKey: sharedStorageKeys.config(config.queryKeyPrefix, serviceId),
    enabled: !!serviceId,
    queryFn: async () => {
      const res = await serviceApiV3.get(`${mediaBase(serviceId, config.kind)}/output-storages`);
      return res.data?.data ?? res.data;
    },
  });
}

// ============================================
// Mutations
// ============================================

export function useCreateOutputStorage(
  serviceId: string,
  config: FileTransferConfig,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  options?: UseMutationOptions<any, Error, CreateOutputStoragePayload>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreateOutputStoragePayload) => {
      const res = await serviceApiV3.post(
        `${mediaBase(serviceId, config.kind)}/output-storages`,
        payload,
      );
      return res.data?.data ?? res.data;
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({
        queryKey: sharedStorageKeys.config(config.queryKeyPrefix, serviceId),
      });
      options?.onSuccess?.(...args);
    },
  });
}

export function useUpdateOutputStorage(
  serviceId: string,
  config: FileTransferConfig,
  options?: UseMutationOptions<
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    any,
    Error,
    { outputStorageType: OutputStorageType; outputStorageId: string; payload: CreateOutputStoragePayload }
  >,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      outputStorageType,
      outputStorageId,
      payload,
    }: {
      outputStorageType: OutputStorageType;
      outputStorageId: string;
      payload: CreateOutputStoragePayload;
    }) => {
      const res = await serviceApiV3.put(
        `${mediaBase(serviceId, config.kind)}/output-storages/${outputStorageType}/${outputStorageId}`,
        payload,
      );
      return res.data?.data ?? res.data;
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({
        queryKey: sharedStorageKeys.config(config.queryKeyPrefix, serviceId),
      });
      options?.onSuccess?.(...args);
    },
  });
}

export function useDeleteOutputStorage(
  serviceId: string,
  config: FileTransferConfig,
  options?: UseMutationOptions<
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    any,
    Error,
    { outputStorageType: OutputStorageType; outputStorageId: string }
  >,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      outputStorageType,
      outputStorageId,
    }: {
      outputStorageType: OutputStorageType;
      outputStorageId: string;
    }) => {
      const res = await serviceApiV3.delete(
        `${mediaBase(serviceId, config.kind)}/output-storages/${outputStorageType}/${outputStorageId}`,
      );
      return res.data?.data ?? res.data;
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({
        queryKey: sharedStorageKeys.config(config.queryKeyPrefix, serviceId),
      });
      options?.onSuccess?.(...args);
    },
  });
}
